import React, { useEffect, useMemo, useState } from 'react';
import { Search, DollarSign, TrendingUp, MessageSquare, Clock, User, Wrench, ChevronRight } from 'lucide-react';
import { mockInteractionsByAgent } from '../data/mockData';
import { Interaction } from '../types';
import { useAgent } from '../contexts/AgentContext';
import { AppCard } from '../components/base/AppCard';
import { AppBadge } from '../components/base/AppBadge';
import { HistoryDetailPanel } from '../components/HistoryDetailPanel';
export function StoricoPage() {
  const {
    activeAgentId
  } = useAgent();
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [selectedInteraction, setSelectedInteraction] = useState<Interaction | null>(null);
  useEffect(() => {
    const history = mockInteractionsByAgent[activeAgentId] || mockInteractionsByAgent['default'];
    setInteractions(history.interactions);
    setSelectedInteraction(null);
  }, [activeAgentId]);
  const filteredInteractions = useMemo(() => {
    return interactions.filter(interaction => {
      const matchesSearch = searchQuery === '' || interaction.userInput.toLowerCase().includes(searchQuery.toLowerCase()) || interaction.userLabel.toLowerCase().includes(searchQuery.toLowerCase());
      let matchesDate = true;
      if (dateRange !== 'all') {
        const now = new Date();
        const interactionDate = new Date(interaction.timestamp);
        const hoursDiff = (now.getTime() - interactionDate.getTime()) / (1000 * 60 * 60);
        if (dateRange === '24h' && hoursDiff > 24) matchesDate = false;
        if (dateRange === '7d' && hoursDiff > 168) matchesDate = false;
        if (dateRange === '30d' && hoursDiff > 720) matchesDate = false;
      }
      return matchesSearch && matchesDate;
    });
  }, [interactions, searchQuery, dateRange]);
  const totalCost = filteredInteractions.reduce((sum, int) => sum + int.costEstimate, 0);
  const mostUsedTools = useMemo(() => {
    const toolCounts: Record<string, number> = {};
    filteredInteractions.forEach(int => {
      int.toolsUsed.forEach(tool => {
        toolCounts[tool] = (toolCounts[tool] || 0) + 1;
      });
    });
    return Object.entries(toolCounts).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([tool]) => tool).join(', ') || 'Nessuno';
  }, [filteredInteractions]);
  return <div className="pb-12 space-y-8" data-id="element-1984">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1985">
        <AppCard className="flex items-center justify-between p-6" data-id="element-1986">
          <div data-id="element-1987">
            <p className="text-3xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight" data-id="element-1988">
              {filteredInteractions.length}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-1989">
              Interazioni totali
            </p>
          </div>
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl" data-id="element-1990">
            <MessageSquare className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-1991" />
          </div>
        </AppCard>

        <AppCard className="flex items-center justify-between p-6" data-id="element-1992">
          <div data-id="element-1993">
            <p className="text-3xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight" data-id="element-1994">
              €{(totalCost / 100).toFixed(2)}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-1995">
              Costo stimato
            </p>
          </div>
          <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-xl" data-id="element-1996">
            <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" data-id="element-1997" />
          </div>
        </AppCard>

        <AppCard className="flex items-center justify-between p-6" data-id="element-1998">
          <div className="flex-1 min-w-0 mr-4" data-id="element-1999">
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate" data-id="element-2000">
              {mostUsedTools}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-2001">
              Strumenti più usati
            </p>
          </div>
          <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-xl flex-shrink-0" data-id="element-2002">
            <TrendingUp className="w-6 h-6 text-gray-600 dark:text-gray-400" data-id="element-2003" />
          </div>
        </AppCard>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4" data-id="element-2004">
        <div className="flex-1 relative" data-id="element-2005">
          <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-3" data-id="element-2006" />
          <input type="text" placeholder="Cerca nelle interazioni..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-180 shadow-sm" data-id="element-2007" />
        </div>

        <select value={dateRange} onChange={e => setDateRange(e.target.value)} className="appearance-none bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle text-gray-700 dark:text-gray-300 py-2.5 px-4 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 cursor-pointer transition-all duration-180 shadow-sm min-w-[180px]" data-id="element-2008">
          <option value="all" data-id="element-2009">Tutto il periodo</option>
          <option value="24h" data-id="element-2010">Ultime 24 ore</option>
          <option value="7d" data-id="element-2011">Ultima settimana</option>
          <option value="30d" data-id="element-2012">Ultimo mese</option>
        </select>
      </div>

      {/* Table */}
      <AppCard padding="none" className="overflow-hidden" data-id="element-2013">
        <div className="overflow-x-auto" data-id="element-2014">
          <table className="w-full" data-id="element-2015">
            <thead className="bg-gray-50 dark:bg-dark-subtle border-b border-gray-100 dark:border-dark-medium" data-id="element-2016">
              <tr data-id="element-2017">
                <th className="px-6 py-4 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2018">
                  Data e ora
                </th>
                <th className="px-6 py-4 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2019">
                  Utente
                </th>
                <th className="px-6 py-4 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2020">
                  Input
                </th>
                <th className="px-6 py-4 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2021">
                  Strumenti
                </th>
                <th className="px-6 py-4 text-right text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2022">
                  Costo
                </th>
                <th className="px-6 py-4 w-10" data-id="element-2023"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-dark-medium" data-id="element-2024">
              {filteredInteractions.length === 0 ? <tr data-id="element-2025">
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400 text-sm" data-id="element-2026">
                    Nessuna interazione trovata
                  </td>
                </tr> : filteredInteractions.map(interaction => <tr key={interaction.id} onClick={() => setSelectedInteraction(interaction)} className="hover:bg-gray-50 dark:hover:bg-dark-hover cursor-pointer transition-colors duration-150" data-id="element-2027">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2028">
                      <div className="flex items-center text-sm text-gray-900 dark:text-gray-100" data-id="element-2029">
                        <Clock className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2" data-id="element-2030" />
                        {new Date(interaction.timestamp).toLocaleString('it-IT', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2031">
                      <div className="flex items-center" data-id="element-2032">
                        <User className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2" data-id="element-2033" />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-2034">
                          {interaction.userLabel}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-2035">
                      <span className="text-sm text-gray-600 dark:text-gray-400 line-clamp-1 max-w-xs" data-id="element-2036">
                        {interaction.userInput}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-2037">
                      <div className="flex flex-wrap gap-1" data-id="element-2038">
                        {interaction.toolsUsed.length > 0 ? <>
                            {interaction.toolsUsed.slice(0, 2).map(tool => <AppBadge key={tool} variant="blue" icon={<Wrench className="w-3 h-3" data-id="element-2040" />} data-id="element-2039">
                                {tool}
                              </AppBadge>)}
                            {interaction.toolsUsed.length > 2 && <span className="text-xs text-gray-500 dark:text-gray-400 ml-1" data-id="element-2041">
                                +{interaction.toolsUsed.length - 2}
                              </span>}
                          </> : <span className="text-xs text-gray-400 dark:text-gray-600" data-id="element-2042">
                            -
                          </span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right" data-id="element-2043">
                      <span className="text-sm font-mono font-medium text-gray-900 dark:text-gray-100" data-id="element-2044">
                        €{(interaction.costEstimate / 100).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-2045">
                      <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500" data-id="element-2046" />
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </AppCard>

      {/* History Detail Panel */}
      <HistoryDetailPanel interaction={selectedInteraction} isOpen={!!selectedInteraction} onClose={() => setSelectedInteraction(null)} data-id="element-2047" />
    </div>;
}